import { useState, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../config/firebase';
import { useEmailCounts } from '../hooks/useEmailCounts';
import Header from './Header';
import Sidebar from './Sidebar';
import EmailList from './EmailList';
import ComposeModal from './ComposeModal';
import EmailDetails from './EmailDetails';
import './Dashboard.css';

function Dashboard({ user }) {
  const [currentFolder, setCurrentFolder] = useState('inbox');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [composeModalOpen, setComposeModalOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { counts: emailCounts } = useEmailCounts(user);

  // Load sidebar preference on mount
  useEffect(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    if (saved === 'true' && window.innerWidth > 768) {
      setSidebarCollapsed(true);
    }
  }, []);

  // Save sidebar preference when it changes (desktop only)
  useEffect(() => {
    if (window.innerWidth > 768) {
      localStorage.setItem('sidebarCollapsed', sidebarCollapsed.toString());
    }
  }, [sidebarCollapsed]);

  // Handle window resize to restore preference when switching to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        const saved = localStorage.getItem('sidebarCollapsed');
        if (saved === 'true') {
          setSidebarCollapsed(true);
        }
        setSidebarVisible(false); // Close mobile sidebar when switching to desktop
      } else {
        setSidebarCollapsed(false); // Always expanded on mobile
        setSidebarVisible(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem('ssoData');
      window.location.reload();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleFolderChange = (folder) => {
    setCurrentFolder(folder);
    setSelectedEmail(null);
    setSearchTerm('');
    if (window.innerWidth <= 768) {
      setSidebarVisible(false);
    }
  };

  const handleCompose = (to = '', subject = '', message = '') => {
    setComposeModalOpen({ to, subject, message });
  };

  const handleEmailClick = (email) => {
    setSelectedEmail(email);
  };

  const handleBackToList = () => {
    setSelectedEmail(null);
  };

  return (
    <div className="dashboard">
      <Header 
        onCompose={() => handleCompose()}
        onMenuToggle={() => {
          if (window.innerWidth <= 768) {
            setSidebarVisible(!sidebarVisible);
          } else {
            const newState = !sidebarCollapsed;
            setSidebarCollapsed(newState);
            localStorage.setItem('sidebarCollapsed', newState.toString());
          }
        }}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        user={user}
        onSignOut={handleSignOut}
      />
      
      <div className="main-content">
        <Sidebar
          currentFolder={currentFolder}
          onFolderChange={handleFolderChange}
          collapsed={sidebarCollapsed}
          visible={sidebarVisible}
          onClose={() => setSidebarVisible(false)}
          emailCounts={emailCounts}
        />
        
        {selectedEmail ? (
          <EmailDetails
            email={selectedEmail}
            folder={currentFolder}
            onBack={handleBackToList}
            onCompose={handleCompose}
            onEmailRead={(emailId) => {
              // Update the selected email's read status
              setSelectedEmail(prev => prev ? { ...prev, read: true } : null);
            }}
            user={user}
          />
        ) : (
          <EmailList
            folder={currentFolder}
            searchTerm={searchTerm}
            onEmailClick={handleEmailClick}
            user={user}
            key={`${currentFolder}-${user?.uid}`}
          />
        )}
      </div>

      {composeModalOpen && (
        <ComposeModal
          initialData={composeModalOpen}
          onClose={() => setComposeModalOpen(false)}
          user={user}
        />
      )}
    </div>
  );
}

export default Dashboard;
